import Loading from '../../components/loading';
import {useMsal} from '@azure/msal-react';
import {useNavigate} from 'react-router';
import auth from '../../lib/auth';
import {AccountInfo, AuthenticationResult, EventType} from '@azure/msal-browser';
import {useEffect, useState} from 'react';
import {silentRequest} from '../../lib/entra-auth/entra-auth-config';

function isTokenExpired(token: string): boolean {
    try {
        const [, payloadBase64] = token.split('.');
        const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
        const payload = JSON.parse(payloadJson);

        const exp = payload.exp;
        if (typeof exp !== 'number') {
            throw new Error("Token missing 'exp' claim");
        }

        const now = Math.floor(Date.now() / 1000);
        return exp < now;
    } catch (error) {
        console.error('Invalid JWT token:', error);
        return true; // Assume expired if token is invalid
    }
}

export default function LogInRedirectPage() {
    const {instance} = useMsal();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    async function getTorchTokenFromEntraID(activeAccount: AccountInfo) {
        if (!activeAccount || !activeAccount.idToken)
            return;

        let idToken = activeAccount.idToken;
        if (isTokenExpired(idToken)) {
            try {
                const result = await instance.acquireTokenSilent({...silentRequest, account: activeAccount});
                idToken = result.idToken;
                if (isTokenExpired(idToken)) throw new Error('Refreshed token is still expired');
            } catch (err) {
                setError('Session expired. Please log in again.');
                setTimeout(() => navigate('/login'), 10000);
                return;
            }
        }

        auth.loginWithEntraIDToken(idToken)
            .then(redirectUserToLandingPage)
            .catch(err => {
                console.error('Error getting Torch token from Entra ID:', err);
                setError('An error occurred while logging in. You will be automatically redirected in 10 seconds to try again.');
                setTimeout(() => {
                    navigate('/login');
                }, 10000); // Redirect back to login page after 10 seconds
            });
    }

    function redirectUserToLandingPage() {
        navigate(auth.getLandingPageUrl());
    }

    if(!auth.isExpired()) {
        redirectUserToLandingPage();
        return null;
    }

    useEffect(() => {
        let callbackId: string | null = null;
        const timeout = setTimeout(() => {
            const activeAccount = instance.getActiveAccount();
            if (activeAccount) {
                getTorchTokenFromEntraID(activeAccount);
            } else {
                callbackId = instance.addEventCallback((event) => {
                    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                        const account = (event.payload as AuthenticationResult).account;
                        if (account) {
                            instance.setActiveAccount(account);
                            getTorchTokenFromEntraID(account);
                        }
                    } else if (event.eventType === EventType.LOGIN_FAILURE) {
                        setError(`Login failed :: ${event.error}`);
                    }
                });
            }
        }, 500); //allow for new token to be set

        return () => {
            clearTimeout(timeout);
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance]);


    return <div className="w-full h-screen flex justify-center items-center flex-col">
        {error && <div className='text-center text-red-500'>{error}</div>}
        {!error && <Loading large />}
    </div>;
}